import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { useListing } from "@/hooks/useListing";
import { useAuth } from "@/hooks/useAuth";
import { CarCards } from "./CarCards";
import { OrdersFilter } from "./OrdersFilter";
import { ContractSignatureSuccessDialog } from "./ContractSignatureSuccessDialog";
import { ContractSignatureFailureDialog } from "./ContractSignatureFailureDialog";
import { Pagination } from "@/components/ui/pagination";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { ContractService } from "@/services/contract/contractService";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

type OrderFilter = "all" | "ej-levererade" | "levererade";

interface OrdersViewProps {
  filter: OrderFilter;
}

function isContractSignatureFailureReturn(sp: URLSearchParams) {
  if (sp.has("error") || sp.has("error_description")) {
    return true;
  }
  const raw = sp.get("contractSignatureStatus");
  if (!raw) return false;
  const v = raw.toLowerCase();
  return v === "declined" || v === "error" || v === "failed" || v === "cancelled" || v === "canceled";
}

export const OrdersView = ({ filter }: OrdersViewProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCar, setSelectedCar] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:orders",
    5,
    [5, 10, 15, 20],
  );
  const [showSignatureSuccess, setShowSignatureSuccess] = useState(false);
  const [showSignatureFailure, setShowSignatureFailure] = useState(false);
  const [iduraReturnDetected, setIduraReturnDetected] = useState(false);
  const finalizeStarted = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    paymentStatus?: "paid" | "pending" | "unpaid" | "overdue";
    sortBy?: string;
  }>({});

  // Fetch data using hooks
  const { user } = useAuth();
  const { getMyOrders } = useListing();
  const searchParams = new URLSearchParams(location.search);
  const hasIduraReturnParams =
    searchParams.has("signatory_id") ||
    searchParams.has("signatory-token") ||
    searchParams.has("contractSignatureStatus") ||
    searchParams.has("contractSigned");

  // Map frontend sortBy to backend sort format
  // For date-based sorts, use server-side sorting
  // For price-based sorts, use client-side sorting
  const getBackendSort = (sortBy: string): string | undefined => {
    switch (sortBy) {
      case "updated":
        return "-updatedAt";
      case "newest":
        return "-createdAt";
      case "oldest":
        return "createdAt";
      case "price-high-low":
      case "price-low-high":
        // Price sorting is done client-side
        return undefined;
      default:
        return "-updatedAt";
    }
  };

  // Get user's orders (listings where user is the seller) - fetch all, paginate on client
  const {
    data: myOrdersData,
    isLoading: isLoadingMyOrders,
    error: myOrdersError,
    refetch: refetchMyOrders,
  } = getMyOrders({
    // TODO: Mock user ID for testing
    page: currentPage,
    limit: itemsPerPage,
    // Apply delivery status based on current tab
    deliveryStatus:
      filter === "levererade"
        ? "delivered"
        : filter === "ej-levererade"
        ? "not_delivered"
        : undefined,
    paymentStatus: appliedFilters.paymentStatus, // Apply the selected payment status filter
    sort: appliedFilters.sortBy ? getBackendSort(appliedFilters.sortBy) : undefined, // Apply server-side sorting for date-based sorts
  });

  useEffect(() => {
    if (!hasIduraReturnParams) {
      finalizeStarted.current = false;
      return;
    }

    // Keep a stable flag so we can clear the URL but still keep polling.
    setIduraReturnDetected(true);

    const sp = new URLSearchParams(location.search);
    const failed = isContractSignatureFailureReturn(sp);
    const alreadySignedFlag = sp.get("contractSigned") === "1";
    const signatoryId = sp.get("signatory_id") || sp.get("signatoryId");

    if (failed) {
      setShowSignatureFailure(true);
      setShowSignatureSuccess(false);
    } else if (alreadySignedFlag) {
      setShowSignatureSuccess(true);
      setShowSignatureFailure(false);
    } else if (!finalizeStarted.current) {
      // Web returns to /orders directly from Idura — finalize here when webhook is missing.
      finalizeStarted.current = true;
      void (async () => {
        try {
          const result = await ContractService.finalizePendingBankIdSignature({
            signatoryId,
          });
          if (result.signed) {
            setShowSignatureSuccess(true);
            setShowSignatureFailure(false);
          } else {
            // Still show success UI if Idura redirected without error; polling may catch webhook.
            setShowSignatureSuccess(true);
            setShowSignatureFailure(false);
          }
        } catch {
          setShowSignatureSuccess(true);
          setShowSignatureFailure(false);
        }
      })();
    }

    const dialogTimer = window.setTimeout(() => {
      setShowSignatureSuccess(false);
      setShowSignatureFailure(false);
    }, 3000);

    navigate(location.pathname, { replace: true });

    return () => {
      window.clearTimeout(dialogTimer);
    };
    // location.search must be watched so warm deep links into /orders update dialogs
  }, [hasIduraReturnParams, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!iduraReturnDetected) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 8;
    let stopped = false;

    const refreshOrders = async () => {
      attempts += 1;
      // Retry finalize a couple of times in case Idura needs a moment after redirect.
      if (attempts <= 3 && user) {
        try {
          await ContractService.finalizePendingBankIdSignature();
        } catch {
          /* ignore — webhook/polling may still succeed */
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ["listings", "my-orders"],
        refetchType: "active",
      });
      await refetchMyOrders();
    };

    void refreshOrders();

    const intervalId = window.setInterval(() => {
      if (stopped) return;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        stopped = true;
        setIduraReturnDetected(false);
        return;
      }
      void refreshOrders();
    }, 1500);

    return () => {
      window.clearInterval(intervalId);
      stopped = true;
    };
  }, [iduraReturnDetected, queryClient, refetchMyOrders, user]);

  // Reset pagination and list filters when delivery tab changes
  const prevFilter = useRef(filter);
  if (prevFilter.current !== filter) {
    setCurrentPage(1);
    setAppliedFilters({});
    prevFilter.current = filter;
  }

  // Get orders and apply client-side sorting for price-based sorts
  const orders = useMemo(() => {
    const ordersList = myOrdersData?.orders || [];
    const currentSortBy = appliedFilters.sortBy || "newest";
    
    // Apply client-side sorting for price-based sorts
    if (currentSortBy === "price-high-low" || currentSortBy === "price-low-high") {
      const getListingPrice = (listing: any) => {
        if (listing.listingType === "auction") {
          const isBuyNowTriggered = listing.auctionDetails?.isBuyNowTriggered === true;
          const auctionPrice = isBuyNowTriggered
            ? (listing.auctionDetails?.currentBuyNowPrice ?? listing.auctionDetails?.buyNowFixed)
            : listing.auctionDetails?.currentBid;
          return auctionPrice || listing.auctionDetails?.startingPrice || 0;
        }

        return listing.directPurchaseDetails?.currentPrice || 0;
      };

      return [...ordersList].sort((a, b) => {
        // Get price for order a
        const aPrice = getListingPrice(a.listing);
        
        // Get price for order b
        const bPrice = getListingPrice(b.listing);
        
        // Sort based on selected option
        if (currentSortBy === "price-high-low") {
          return bPrice - aPrice; // Descending
        } else {
          return aPrice - bPrice; // Ascending
        }
      });
    }
    
    return ordersList;
  }, [myOrdersData?.orders, appliedFilters.sortBy]);

  // Pagination logic - use server data
  const totalPages = Math.ceil((myOrdersData?.total || 0) / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSelectedCar(null); // Reset selected car when changing pages
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    setSelectedCar(null);
  };

  const handleApplyFilters = (filters: {
    paymentStatus?: "paid" | "pending" | "unpaid" | "overdue";
    sortBy?: string;
  }) => {
    setAppliedFilters(filters);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  if (isLoadingMyOrders) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{o.loading.orders}</div>
      </div>
    );
  }

  if (myOrdersError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">
          {o.error.fetchOrders}
        </div>
      </div>
    );
  }

  return (
    <div>
      <ContractSignatureSuccessDialog
        isOpen={showSignatureSuccess}
        onClose={() => setShowSignatureSuccess(false)}
      />
      <ContractSignatureFailureDialog
        isOpen={showSignatureFailure}
        onClose={() => setShowSignatureFailure(false)}
      />
      {/* Filter Component */}
      <OrdersFilter
        currentFilter={filter}
        appliedPaymentStatus={appliedFilters.paymentStatus}
        appliedSortBy={appliedFilters.sortBy}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {o.showing.replace("{count}", String(orders.length)).replace("{total}", String(myOrdersData?.total || 0))}
        </div>
      </div>

      <CarCards
        orders={orders}
        selectedCar={selectedCar}
        setSelectedCar={setSelectedCar}
      />

      {/* Pagination */}
      {orders.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={myOrdersData?.total || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[5, 10, 15, 20]}
        />
      )}
    </div>
  );
};
